import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useT } from '../i18n';

export interface ToastInput {
  message: string;
  /** Shows an Undo button for the toast's lifetime (PRD: 10-second Undo on every save). */
  onUndo?: () => void;
  tone?: 'default' | 'error';
  durationMs?: number;
}

interface ToastItem extends ToastInput {
  id: number;
  at: number;
}

const Ctx = createContext<(t: ToastInput) => void>(() => undefined);
export const UNDO_MS = 10_000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const seq = useRef(0);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());
  const t = useT();

  const dismiss = useCallback((id: number) => {
    clearTimeout(timers.current.get(id));
    timers.current.delete(id);
    setItems((xs) => xs.filter((x) => x.id !== id));
  }, []);

  const push = useCallback(
    (input: ToastInput) => {
      const id = ++seq.current;
      const duration = input.durationMs ?? (input.onUndo ? UNDO_MS : 4_000);
      // One undo-able toast at a time: a new save replaces the previous toast.
      setItems((xs) => [
        ...xs.filter((x) => x.tone === 'error').slice(-1),
        { ...input, id, at: Date.now(), durationMs: duration },
      ]);
      timers.current.set(
        id,
        setTimeout(() => dismiss(id), duration),
      );
    },
    [dismiss],
  );

  useEffect(() => () => timers.current.forEach((tm) => clearTimeout(tm)), []);

  return (
    <Ctx.Provider value={push}>
      {children}
      <div className="toasts" role="status" aria-live="polite">
        {items.map((x) => (
          <div key={x.id} className={`toast${x.tone === 'error' ? ' toast--error' : ''}`}>
            <span className="toast__msg">{x.message}</span>
            {x.onUndo && (
              <button
                type="button"
                onClick={() => {
                  x.onUndo!();
                  dismiss(x.id);
                  push({ message: t('common.undone'), durationMs: 2_500 });
                }}
              >
                {t('common.undo')}
              </button>
            )}
            {x.onUndo && <span className="toast__bar" style={{ animationDuration: `${x.durationMs}ms` }} />}
          </div>
        ))}
      </div>
    </Ctx.Provider>
  );
}

export const useToast = () => useContext(Ctx);

export function useStableToast() {
  const toast = useToast();
  return useMemo(() => toast, [toast]);
}
